import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Folder as FolderIcon } from '@mui/icons-material';
import { getProjects, getProjectCostSheets, getProjectCostSheetDocuments, getProjectCostSheetCategories, createCostSheet, updateCostSheet, deleteCostSheet, getCostSheetTemplates } from '../services/api';
import { UNITS } from '../constants/units';
import { ITEM_TYPES } from '../constants/itemTypes';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../utils/dateUtils';

const UNIT_VALUES = new Set(Object.values(UNITS));

/** Map backend / upload units (e.g. m2, m3) to app UNITS labels (m², m³). */
function resolveCostSheetUnit(unit) {
  if (unit == null || unit === '') return Object.values(UNITS)[0];
  const trimmed = String(unit).trim();
  const lower = trimmed.toLowerCase();
  if (lower === 'm2' || lower === 'm^2' || lower === 'sqm') return UNITS.SQUARE_METER;
  if (lower === 'm3' || lower === 'm^3' || lower === 'cbm') return UNITS.CUBIC_METER;
  if (UNIT_VALUES.has(trimmed)) return trimmed;
  return trimmed;
}

/** Hierarchical numeric sort for codes like 1.0, 1.2, 2.0, 10.1.1 (not lexicographic). */
function compareCodeSort(a, b) {
  const key = (code) => {
    if (code == null || String(code).trim() === '') return [];
    const parts = String(code).match(/\d+/g);
    if (!parts || parts.length === 0) return [Number.MAX_SAFE_INTEGER];
    return parts.map((p) => parseInt(p, 10));
  };
  const aa = key(a);
  const bb = key(b);
  const len = Math.max(aa.length, bb.length);
  for (let i = 0; i < len; i++) {
    const x = aa[i];
    const y = bb[i];
    if (x == null && y == null) return 0;
    if (x == null) return -1;
    if (y == null) return 1;
    if (x !== y) return x - y;
  }
  return 0;
}

function compareCostSheetOrderRows(a, b) {
  const aOrder = a?.sortOrder;
  const bOrder = b?.sortOrder;
  if (aOrder != null && bOrder != null) return aOrder - bOrder;
  if (aOrder != null) return -1;
  if (bOrder != null) return 1;
  const codeCmp = compareCodeSort(a?.code, b?.code);
  if (codeCmp !== 0) return codeCmp;
  if (a?.rowType === 'category' && b?.rowType !== 'category') return -1;
  if (a?.rowType !== 'category' && b?.rowType === 'category') return 1;
  const aId = typeof a?.id === 'number' ? a.id : 0;
  const bId = typeof b?.id === 'number' ? b.id : 0;
  return aId - bId;
}

/** Sort by document name, then Excel sortOrder within each document. Manual entries last. */
function compareAllTabRows(a, b) {
  const aManual = a?.documentId == null;
  const bManual = b?.documentId == null;
  if (aManual !== bManual) return aManual ? 1 : -1;

  const aDoc = String(a?.documentName || '');
  const bDoc = String(b?.documentName || '');
  const docCmp = aDoc.localeCompare(bDoc, undefined, { numeric: true, sensitivity: 'base' });
  if (docCmp !== 0) return docCmp;

  return compareCostSheetOrderRows(a, b);
}

function isCategoryRow(row) {
  return row?.rowType === 'category';
}

function buildInterleavedDisplayRows(categories, costSheets, { documentNameById, manualLabel, sortByDocument } = {}) {
  const resolveDocumentName = (documentId) => {
    if (documentId == null) return manualLabel || '';
    return documentNameById?.get(documentId) || '';
  };

  const rows = [
    ...categories.map((cat) => ({
      id: `category-${cat.id}`,
      rowType: 'category',
      documentId: cat.documentId ?? null,
      documentName: resolveDocumentName(cat.documentId),
      sortOrder: cat.sortOrder ?? null,
      name: cat.name || '',
      code: cat.code || '',
      level: cat.level ?? 0,
      notes: cat.notes || '',
      amount: cat.amount,
      price: cat.price,
      total: cat.total,
      unit: cat.unit,
    })),
    ...costSheets.map((sheet) => ({
      ...sheet,
      rowType: 'line',
      documentId: sheet.documentId ?? sheet.document?.id ?? null,
      documentName: sheet.document?.name || resolveDocumentName(sheet.documentId ?? sheet.document?.id),
    })),
  ];
  return rows.sort(sortByDocument ? compareAllTabRows : compareCostSheetOrderRows);
}

const MANUAL_DOCUMENT_KEY = 'manual';

function CostSheets() {
  const { t } = useTranslation();
  const [costSheets, setCostSheets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [viewTab, setViewTab] = useState(0);
  const [selectedDocumentKey, setSelectedDocumentKey] = useState('');
  const [selectedSourceSheet, setSelectedSourceSheet] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingCostSheet, setEditingCostSheet] = useState(null);
  const [formData, setFormData] = useState({
    projectId: '',
    name: '',
    code: '',
    categoryId: '',
    itemType: Object.values(ITEM_TYPES)[0],
    unit: Object.values(UNITS)[0],
    quantity: '',
    unitPrice: '',
    totalPrice: '',
    workType: '',
    notes: '',
    templateId: '',
  });
  const [templates, setTemplates] = useState([]);

  const manualEntryCount = useMemo(
    () => costSheets.filter((sheet) => !sheet.documentId).length,
    [costSheets]
  );

  const documentOptions = useMemo(() => {
    const options = documents.map((doc) => ({
      key: String(doc.id),
      label: doc.name,
      meta: t('costSheetsPage.itemCount', { count: doc.costSheetCount }),
      uploadedAt: doc.uploadedAt,
    }));
    if (manualEntryCount > 0) {
      options.push({
        key: MANUAL_DOCUMENT_KEY,
        label: t('costSheetsPage.manualEntries'),
        meta: t('costSheetsPage.itemCount', { count: manualEntryCount }),
        uploadedAt: null,
      });
    }
    return options;
  }, [documents, manualEntryCount, t]);

  const filteredCostSheets = useMemo(() => {
    let rows = costSheets;
    if (viewTab === 0) {
      if (!selectedDocumentKey) return [];
      if (selectedDocumentKey === MANUAL_DOCUMENT_KEY) {
        rows = costSheets.filter((sheet) => !sheet.documentId);
      } else {
        const documentId = parseInt(selectedDocumentKey, 10);
        rows = costSheets.filter((sheet) => sheet.documentId === documentId);
      }
      if (selectedSourceSheet) {
        rows = rows.filter((sheet) => sheet.sourceSheet === selectedSourceSheet);
      }
      return rows;
    }
    return costSheets;
  }, [costSheets, viewTab, selectedDocumentKey, selectedSourceSheet]);

  const documentNameById = useMemo(() => {
    const map = new Map();
    documents.forEach((doc) => map.set(doc.id, doc.name));
    return map;
  }, [documents]);

  const filteredCategories = useMemo(() => {
    if (viewTab === 0) {
      if (!selectedDocumentKey || selectedDocumentKey === MANUAL_DOCUMENT_KEY) return [];
      let rows = categories.filter((cat) => cat.documentId === parseInt(selectedDocumentKey, 10));
      if (selectedSourceSheet) {
        rows = rows.filter((cat) => cat.sourceSheet === selectedSourceSheet);
      }
      return rows;
    }
    // All tab: include categories that belong to a document
    return categories.filter((cat) => cat.documentId != null);
  }, [categories, viewTab, selectedDocumentKey, selectedSourceSheet]);

  const displayRows = useMemo(() => {
    const manualLabel = t('costSheetsPage.manualEntries');
    if (viewTab === 1) {
      return buildInterleavedDisplayRows(filteredCategories, filteredCostSheets, {
        documentNameById,
        manualLabel,
        sortByDocument: true,
      });
    }
    if (viewTab === 0 && selectedDocumentKey && selectedDocumentKey !== MANUAL_DOCUMENT_KEY) {
      return buildInterleavedDisplayRows(filteredCategories, filteredCostSheets, {
        documentNameById,
        manualLabel,
        sortByDocument: false,
      });
    }
    return filteredCostSheets.map((sheet) => ({
      ...sheet,
      rowType: 'line',
      documentName: sheet.document?.name || manualLabel,
    }));
  }, [viewTab, selectedDocumentKey, filteredCategories, filteredCostSheets, documentNameById, t]);

  const worksheetOptions = useMemo(() => {
    if (viewTab !== 0 || !selectedDocumentKey || selectedDocumentKey === MANUAL_DOCUMENT_KEY) {
      return [];
    }
    const documentId = parseInt(selectedDocumentKey, 10);
    const names = [...new Set(
      costSheets
        .filter((sheet) => sheet.documentId === documentId && sheet.sourceSheet)
        .map((sheet) => sheet.sourceSheet)
    )].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    return names;
  }, [costSheets, selectedDocumentKey, viewTab]);

  const selectedDocumentNotes = useMemo(() => {
    if (viewTab !== 0 || selectedDocumentKey === MANUAL_DOCUMENT_KEY || !selectedDocumentKey) {
      return null;
    }
    const doc = documents.find((item) => String(item.id) === selectedDocumentKey);
    const notes = doc?.notes?.trim();
    return notes || null;
  }, [documents, selectedDocumentKey, viewTab]);

  useEffect(() => {
    loadProjects();
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      loadCostSheets();
      loadDocuments();
    } else {
      setCostSheets([]);
      setCategories([]);
      setDocuments([]);
      setSelectedDocumentKey('');
    }
  }, [selectedProject]);

  useEffect(() => {
    if (viewTab !== 0 || documentOptions.length === 0) return;
    const hasCurrent = documentOptions.some((option) => option.key === selectedDocumentKey);
    if (!hasCurrent) {
      setSelectedDocumentKey(documentOptions[0].key);
    }
  }, [documentOptions, selectedDocumentKey, viewTab]);

  useEffect(() => {
    setSelectedSourceSheet('');
  }, [selectedDocumentKey]);

  const loadProjects = async () => {
    try {
      const projectsResponse = await getProjects();
      setProjects(projectsResponse.data);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadDocuments = async () => {
    if (!selectedProject) return;
    try {
      const response = await getProjectCostSheetDocuments(selectedProject);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error loading cost sheet documents:', error);
      setDocuments([]);
    }
  };

  const loadCostSheets = useCallback(async () => {
    if (!selectedProject) return;
    
    setLoading(true);
    try {
      const [costSheetsResponse, categoriesResponse] = await Promise.all([
        getProjectCostSheets(selectedProject),
        getProjectCostSheetCategories(selectedProject, { flat: 'true' }),
      ]);
      const processedCostSheets = costSheetsResponse.data.map(sheet => {
        const unit = resolveCostSheetUnit(sheet.unit);

        const processedSheet = {
          ...sheet,
          documentId: sheet.documentId ?? sheet.document?.id ?? null,
          document: sheet.document || null,
          sourceSheet: sheet.sourceSheet || null,
          sortOrder: sheet.sortOrder ?? null,
          category: sheet.category, // Include category information
          name: sheet.name || '',
          code: sheet.code || '',
          unit: unit,
          quantity: Number(sheet.quantity) || 0,
          unitPrice: Number(sheet.unitPrice) || 0,
          totalPrice: Number(sheet.totalPrice) || 0,
          itemType: sheet.itemType || Object.values(ITEM_TYPES)[0],
          workType: sheet.workType || '',
          notes: sheet.notes || '',
        };
        return processedSheet;
      });
      setCostSheets(processedCostSheets);
      setCategories(categoriesResponse.data || []);
    } catch (error) {
      console.error('Error loading cost sheets:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  const fetchTemplates = async () => {
    try {
      const response = await getCostSheetTemplates();
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setEditingCostSheet(null);
    setFormData({
      projectId: selectedProject,
      name: '',
      code: '',
      categoryId: '',
      itemType: Object.values(ITEM_TYPES)[0],
      unit: Object.values(UNITS)[0],
      quantity: '',
      unitPrice: '',
      totalPrice: '',
      workType: '',
      notes: '',
      templateId: '',
    });
  };

  const handleClose = () => {
    setOpen(false);
    setEditingCostSheet(null);
  };

  const handleEdit = useCallback((costSheet) => {
    setEditingCostSheet(costSheet);
    setFormData({
      projectId: costSheet.projectId,
      name: costSheet.name || '',
      code: costSheet.code || '',
      categoryId: costSheet.categoryId || costSheet.category?.id || '',
      itemType: costSheet.itemType,
      unit: costSheet.unit,
      quantity: costSheet.quantity,
      unitPrice: costSheet.unitPrice,
      totalPrice: costSheet.totalPrice,
      workType: costSheet.workType,
      notes: costSheet.notes || '',
      templateId: costSheet.templateId || '',
    });
    setOpen(true);
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (window.confirm(t('costSheetsPage.deleteConfirm'))) {
      try {
        await deleteCostSheet(id);
        loadCostSheets();
      } catch (error) {
        console.error('Error deleting cost sheet:', error);
      }
    }
  }, [t, loadCostSheets]);

  const baseColumns = useMemo(() => [
    {
      field: 'code',
      headerName: t('costSheetsPage.code', { defaultValue: 'Code' }),
      width: 140,
      valueGetter: (params) => params.row.code || '',
      sortComparator: (v1, v2) => compareCodeSort(v1, v2),
      renderCell: (params) => (
        <div style={{
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          lineHeight: '1.4',
          padding: '8px 0',
          fontWeight: isCategoryRow(params.row) ? 600 : undefined,
        }}
        >
          {params.value}
        </div>
      )
    },
    {
      field: 'name',
      headerName: t('projects.fields.name'),
      width: 350,
      valueGetter: (params) => params.row.name || '',
      renderCell: (params) => {
        const notes = params.row.notes?.trim();
        if (isCategoryRow(params.row)) {
          const indent = (params.row.level ?? 0) * 24;
          return (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pl: `${indent}px`, py: 1 }}>
              <FolderIcon fontSize="small" color="primary" sx={{ opacity: 0.8, mt: 0.25 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {params.row.name}
                </Typography>
                {notes && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', whiteSpace: 'pre-wrap', mt: 0.25 }}
                  >
                    {notes}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }

        // Indent under parent category: children sit one step deeper than the folder
        const categoryLevel = params.row.category?.level ?? 0;
        const indent = params.row.categoryId != null ? (categoryLevel + 1) * 24 : 0;

        return (
          <Box sx={{ py: 1, pl: `${indent}px`, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'normal', wordWrap: 'break-word', lineHeight: 1.4 }}
            >
              {params.value}
            </Typography>
            {notes && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', whiteSpace: 'pre-wrap', mt: 0.25 }}
              >
                {notes}
              </Typography>
            )}
          </Box>
        );
      }
    },
    {
      field: 'quantity',
      headerName: t('costSheetsPage.quantity', { defaultValue: 'Quantity' }),
      width: 110,
      valueGetter: (params) => (
        isCategoryRow(params.row) ? params.row.amount : params.row.quantity
      ),
      valueFormatter: (params) => {
        if (params.value == null || params.value === '') return '';
        return Number(params.value).toFixed(2);
      },
    },
    {
      field: 'unit',
      headerName: t('costSheetsPage.unit', { defaultValue: 'Unit' }),
      width: 90,
      valueGetter: (params) => {
        const unit = params.row.unit;
        if (unit == null || unit === '') return '';
        return resolveCostSheetUnit(unit);
      },
    },
    {
      field: 'unitPrice',
      headerName: t('costSheetsPage.unitPrice', { defaultValue: 'Unit Price' }),
      width: 130,
      valueGetter: (params) => (
        isCategoryRow(params.row) ? params.row.price : params.row.unitPrice
      ),
      valueFormatter: (params) => {
        if (params.value == null || params.value === '') return '';
        return formatCurrency(params.value);
      },
    },
    {
      field: 'totalPrice',
      headerName: t('costSheetsPage.totalPrice', { defaultValue: 'Total Price' }),
      width: 130,
      valueGetter: (params) => (
        isCategoryRow(params.row) ? params.row.total : params.row.totalPrice
      ),
      valueFormatter: (params) => {
        if (params.value == null || params.value === '') return '';
        return formatCurrency(params.value);
      },
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 200,
      renderCell: (params) => {
        if (isCategoryRow(params.row)) return null;
        return (
        <Box>
          <Button onClick={() => handleEdit(params.row)}>{t('common.edit')}</Button>
          <Button color="error" onClick={() => handleDelete(params.row.id)}>{t('common.delete')}</Button>
        </Box>
        );
      },
    },
  ], [t, handleEdit, handleDelete]);

  const columns = useMemo(
    () => {
      const sortOrderColumn = {
        field: 'sortOrder',
        headerName: 'Order',
        width: 1,
        sortable: true,
        valueGetter: (params) => params.row.sortOrder ?? Number.MAX_SAFE_INTEGER,
        sortComparator: (_v1, _v2, param1, param2) => {
          const rowA = param1.api.getRow(param1.id);
          const rowB = param2.api.getRow(param2.id);
          return viewTab === 1
            ? compareAllTabRows(rowA, rowB)
            : compareCostSheetOrderRows(rowA, rowB);
        },
      };
      return [sortOrderColumn, ...baseColumns];
    },
    [viewTab, baseColumns]
  );

  const calculateTotalPrice = () => {
    const quantity = parseFloat(formData.quantity) || 0;
    const unitPrice = parseFloat(formData.unitPrice) || 0;
    return (quantity * unitPrice).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        quantity: parseFloat(formData.quantity),
        unitPrice: parseFloat(formData.unitPrice),
        totalPrice: parseFloat(calculateTotalPrice()),
        unit: formData.unit,
        categoryId: formData.categoryId || null,
        templateId: formData.templateId || null,
      };
      
      if (editingCostSheet) {
        await updateCostSheet(editingCostSheet.id, submitData);
      } else {
        await createCostSheet(submitData);
      }
      handleClose();
      loadCostSheets();
    } catch (error) {
      console.error('Error saving cost sheet:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Automatically calculate total price when quantity or unit price changes
      if (name === 'quantity' || name === 'unitPrice') {
        const quantity = name === 'quantity' ? parseFloat(value) || 0 : parseFloat(prev.quantity) || 0;
        const unitPrice = name === 'unitPrice' ? parseFloat(value) || 0 : parseFloat(prev.unitPrice) || 0;
        updated.totalPrice = (quantity * unitPrice).toFixed(2);
      }
      
      return updated;
    });
  };

  const handleProjectChange = (event) => {
    setSelectedProject(event.target.value);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('costSheetsPage.title')}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>{t('costSheetsPage.selectProject')}</InputLabel>
            <Select
              value={selectedProject}
              onChange={handleProjectChange}
              label={t('costSheetsPage.selectProject')}
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpen}
            disabled={!selectedProject}
            sx={{ mb: 2 }}
          >
            {t('costSheetsPage.add')}
          </Button>
        </Grid>
      </Grid>

      {selectedProject && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={viewTab} onChange={(_, value) => setViewTab(value)}>
            <Tab label={t('costSheetsPage.perDocument')} />
            <Tab label={t('costSheetsPage.allGroup')} />
          </Tabs>
        </Box>
      )}

      {selectedProject && viewTab === 0 && documentOptions.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('costSheetsPage.selectDocument')}</InputLabel>
              <Select
                value={selectedDocumentKey}
                onChange={(event) => setSelectedDocumentKey(event.target.value)}
                label={t('costSheetsPage.selectDocument')}
              >
                {documentOptions.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label} ({option.meta})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {worksheetOptions.length > 0 && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('costSheetsPage.selectWorksheet')}</InputLabel>
                <Select
                  value={selectedSourceSheet}
                  onChange={(event) => setSelectedSourceSheet(event.target.value)}
                  label={t('costSheetsPage.selectWorksheet')}
                >
                  <MenuItem value="">{t('costSheetsPage.allWorksheets')}</MenuItem>
                  {worksheetOptions.map((sheetName) => (
                    <MenuItem key={sheetName} value={sheetName}>
                      {sheetName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      )}

      {selectedProject && viewTab === 0 && selectedDocumentNotes && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('costSheetsPage.documentNotes')}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {selectedDocumentNotes}
          </Typography>
        </Paper>
      )}

      <DataGrid
        rows={displayRows}
        columns={columns}
        pageSize={10}
        loading={loading}
        autoHeight
        sortingMode="client"
        isRowSelectable={(params) => !isCategoryRow(params.row)}
        getRowClassName={(params) => (
          isCategoryRow(params.row) ? 'cost-sheet-category-row' : ''
        )}
        initialState={{
          sorting: {
            sortModel: [{ field: 'sortOrder', sort: 'asc' }],
          },
          columns: {
            columnVisibilityModel: {
              sortOrder: false,
            },
          },
        }}
        disableColumnMenu={false}
        getRowHeight={() => 'auto'}
        sx={{
          '& .cost-sheet-category-row': {
            backgroundColor: 'action.hover',
          },
          '& .cost-sheet-category-row:hover': {
            backgroundColor: 'action.selected',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            padding: '8px 16px',
          },
          '& .MuiDataGrid-row': {
            minHeight: '48px !important',
          }
        }}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCostSheet ? t('costSheetsPage.edit') : t('costSheetsPage.add')}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              name="name"
              label="Name"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              name="code"
              label="Code (optional)"
              value={formData.code}
              onChange={handleChange}
            />
            <TextField
              select
              fullWidth
              margin="normal"
              name="categoryId"
              label={t('costSheetsPage.category')}
              value={formData.categoryId}
              onChange={handleChange}
              helperText={
                categories.length === 0
                  ? t('costSheetsPage.categoryFallbackHint', {
                      defaultValue: 'No categories yet — a Manual entries category will be created automatically.',
                    })
                  : undefined
              }
            >
              <MenuItem value="">
                {t('costSheetsPage.uncategorized')}
              </MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {`${'\u00A0'.repeat((category.level || 0) * 2)}${category.code ? `${category.code} — ` : ''}${category.name}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin="normal"
              name="projectId"
              label={t('projectTimelines.selectProject')}
              value={projects.find(p => p.id === formData.projectId)?.name || ''}
              InputProps={{ readOnly: true }}
              helperText={t('costSheetsPage.projectPreselected', { defaultValue: 'Project is pre-selected based on your selection above' })}
            />
            <TextField
              select
              fullWidth
              margin="normal"
              name="itemType"
              label="Item Type"
              value={formData.itemType}
              onChange={handleChange}
              required
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      maxHeight: 300
                    }
                  }
                }
              }}
            >
              {Object.entries(ITEM_TYPES).map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin="normal"
              name="workType"
              label="Work Type"
              value={formData.workType}
              onChange={handleChange}
              required
            />
            <TextField
              select
              fullWidth
              margin="normal"
              name="unit"
              label="Unit"
              value={formData.unit}
              onChange={handleChange}
              required
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      maxHeight: 300
                    }
                  }
                }
              }}
            >
              {Object.entries(UNITS).map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin="normal"
              name="quantity"
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange}
              required
              inputProps={{ min: 0, step: "0.01" }}
            />
            <TextField
              fullWidth
              margin="normal"
              name="unitPrice"
              label="Unit Price (EUR)"
              type="number"
              value={formData.unitPrice}
              onChange={handleChange}
              required
              inputProps={{ min: 0, step: "0.01" }}
            />
            <TextField
              fullWidth
              margin="normal"
              name="totalPrice"
              label="Total Price (EUR)"
              type="number"
              value={calculateTotalPrice()}
              InputProps={{ readOnly: true }}
            />
            <TextField
              fullWidth
              margin="normal"
              name="notes"
              label={t('personnel.fields.notes')}
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange}
            />
            <TextField
              select
              fullWidth
              margin="normal"
              name="templateId"
              label="Template (optional)"
              value={formData.templateId}
              onChange={handleChange}
            >
              <MenuItem value="">None</MenuItem>
              {templates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">{t('common.save')}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default CostSheets; 